import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ressetUpadtePassword, updatePasswordRequest } from '../../redux/modules/auth/authSlice';
import { toast } from 'react-toastify';
import {
    Box, FormControl, FormHelperText, Grid, OutlinedInput, Typography,
    InputAdornment, IconButton, Paper, LinearProgress, List, ListItem,
    ListItemIcon, ListItemText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

// Password requirements
const PASSWORD_REQUIREMENTS = [
    { key: 'minLength', label: 'At least 8 characters', test: (pwd) => pwd.length >= 8 },
    { key: 'hasUppercase', label: 'One uppercase letter', test: (pwd) => /[A-Z]/.test(pwd) },
    { key: 'hasLowercase', label: 'One lowercase letter', test: (pwd) => /[a-z]/.test(pwd) },
    { key: 'hasNumber', label: 'One number', test: (pwd) => /\d/.test(pwd) },
    { key: 'hasSpecial', label: 'One special character (!@#$%^&*)', test: (pwd) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd) },
];

// Validation schema
const schema = yup.object().shape({
    password: yup
        .string()
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/\d/, 'Password must contain at least one number')
        .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character'),
    confirmPassword: yup
        .string()
        .oneOf([yup.ref('password'), null], 'Passwords must match')
        .required('Confirm Password is required')
});

// Styled components defined outside the component to prevent re-creation on each render
const PageContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(3),
}));

const FormCard = styled(Paper)(({ theme }) => ({
    width: '100%',
    maxWidth: 480,
    borderRadius: 16,
    padding: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3),
        borderRadius: 12,
    },
}));

const FormLabel = styled(Typography)(({ theme }) => ({
    fontSize: 16,
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
}));

const SubmitButton = styled('button')(({ theme }) => ({
    width: '100%',
    padding: '14px 24px',
    border: 'none',
    borderRadius: 10,
    backgroundColor: theme.palette.primary.main,
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
    },
    '&:disabled': {
        backgroundColor: theme.palette.grey[400],
        cursor: 'not-allowed',
    },
}));

// Decode JWT token to get user role (without verifying - just for display purposes)
const decodeToken = (token) => {
    try {
        const payload = token.split('.')[1];
        const decoded = JSON.parse(atob(payload));
        return decoded;
    } catch {
        return null;
    }
};

const Newpassword = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { token } = useParams();
    const { loading, updatePasswordRes } = useSelector((state) => state.auth);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordValue, setPasswordValue] = useState('');

    // Decode token to determine user type for login instructions
    const tokenData = decodeToken(token);
    const isPatient = tokenData?.role === 'patient';
    const loginType = isPatient ? 'Patient' : 'Staff';

    const { register, handleSubmit, formState: { errors }, watch } = useForm({
        resolver: yupResolver(schema),
        mode: 'onChange'
    });

    // Watch password field for live validation display
    const watchedPassword = watch('password', '');

    useEffect(() => {
        setPasswordValue(watchedPassword || '');
    }, [watchedPassword]);

    useEffect(() => {
        if (!updatePasswordRes) return;
        if (updatePasswordRes?.success) {
            toast.success('Password set successfully! Please login with your new password.');
            navigate('/login');
        } else {
            toast.error(updatePasswordRes?.message || 'Failed to set password');
        }
        dispatch(ressetUpadtePassword());
    }, [updatePasswordRes, navigate, dispatch]);

    const onSubmit = (data) => {
        dispatch(updatePasswordRequest({ ...data, Authorization: token }));
    };

    // Calculate password strength
    const getPasswordStrength = () => {
        if (!passwordValue) return 0;
        const passedRequirements = PASSWORD_REQUIREMENTS.filter(req => req.test(passwordValue)).length;
        return (passedRequirements / PASSWORD_REQUIREMENTS.length) * 100;
    };

    const getStrengthColor = () => {
        const strength = getPasswordStrength();
        if (strength < 50) return 'error';
        if (strength < 100) return 'warning';
        return 'success';
    };

    return (
        <PageContainer>
            <FormCard elevation={3}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography
                        variant="h4"
                        fontWeight={600}
                        color="primary.main"
                        gutterBottom
                    >
                        Set New Password
                    </Typography>
                    <Typography color="text.secondary">
                        Create a secure password for your account
                    </Typography>
                </Box>

                <form onSubmit={handleSubmit(onSubmit)}>
                    {/* Password Field */}
                    <Box sx={{ mb: 3 }}>
                        <FormLabel>New Password</FormLabel>
                        <FormControl fullWidth error={Boolean(errors.password)}>
                            <OutlinedInput
                                type={showPassword ? 'text' : 'password'}
                                {...register('password')}
                                placeholder="Enter new password"
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            tabIndex={-1}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                                sx={{ borderRadius: 2 }}
                            />
                            {errors?.password?.message && (
                                <FormHelperText>{errors.password.message}</FormHelperText>
                            )}
                        </FormControl>

                        {/* Password Strength Indicator */}
                        {passwordValue && (
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                        Password Strength:
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={getPasswordStrength()}
                                        color={getStrengthColor()}
                                        sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                                    />
                                </Box>
                                <List dense sx={{ py: 0 }}>
                                    {PASSWORD_REQUIREMENTS.map((req) => {
                                        const passed = req.test(passwordValue);
                                        return (
                                            <ListItem key={req.key} sx={{ py: 0.25, px: 0 }}>
                                                <ListItemIcon sx={{ minWidth: 28 }}>
                                                    {passed ? (
                                                        <CheckCircleIcon fontSize="small" color="success" />
                                                    ) : (
                                                        <CancelIcon fontSize="small" color="error" />
                                                    )}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={req.label}
                                                    primaryTypographyProps={{
                                                        variant: 'caption',
                                                        color: passed ? 'success.main' : 'text.secondary'
                                                    }}
                                                />
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </Box>
                        )}
                    </Box>

                    {/* Confirm Password Field */}
                    <Box sx={{ mb: 4 }}>
                        <FormLabel>Confirm Password</FormLabel>
                        <FormControl fullWidth error={Boolean(errors.confirmPassword)}>
                            <OutlinedInput
                                type={showConfirmPassword ? 'text' : 'password'}
                                {...register('confirmPassword')}
                                placeholder="Confirm your password"
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                            tabIndex={-1}
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                                sx={{ borderRadius: 2 }}
                            />
                            {errors?.confirmPassword?.message && (
                                <FormHelperText>{errors.confirmPassword.message}</FormHelperText>
                            )}
                        </FormControl>
                    </Box>

                    {/* Submit Button */}
                    <SubmitButton type="submit" disabled={loading}>
                        {loading ? 'Setting Password...' : 'Set Password'}
                    </SubmitButton>
                </form>

                {/* Login Instructions */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" align="center">
                        After setting your password, select{' '}
                        <Typography component="span" variant="body2" fontWeight={600} color="primary.main">
                            {loginType}
                        </Typography>{' '}
                        on the login page to access your account.
                    </Typography>
                </Box>
            </FormCard>
        </PageContainer>
    );
};

export default Newpassword;
